const PHQ9_OPTIONS = [
  { value: 0, label: "Not at all" },
  { value: 1, label: "Several days" },
  { value: 2, label: "More than half the days" },
  { value: 3, label: "Nearly every day" },
];

const YES_NO_OPTIONS = [
  { value: 1, label: "Yes" },
  { value: 0, label: "No" },
];

const FUNCTION_OPTIONS = [
  { value: 0, label: "Independent" },
  { value: 1, label: "Needs assistance" },
  { value: 2, label: "Dependent" },
];

export const USA_QUESTIONNAIRES = {
  what_matters: {
    id: "what_matters",
    title: "What Matters",
    type: "yes_no",
    items: [
      { id: "goals_documented", question: "Patient goals and care priorities were discussed and documented." },
      { id: "decision_maker_documented", question: "Decision-maker or surrogate information is documented." },
    ],
  },
  medication_review: {
    id: "medication_review",
    title: "Medication Review",
    type: "yes_no",
    items: [
      { id: "med_rec_completed", question: "Medication reconciliation completed." },
      { id: "high_risk_meds_reviewed", question: "High-risk medications were reviewed for safety." },
      { id: "deprescribing_considered", question: "Deprescribing opportunities were considered." },
    ],
  },
  adl_iadl: {
    id: "adl_iadl",
    title: "Functional Status (ADL/IADL)",
    type: "single_choice",
    options: FUNCTION_OPTIONS,
    stem: "Current level of functional ability in daily activities:",
    items: [
      { id: "adl_bathing", question: "Bathing" },
      { id: "adl_dressing", question: "Dressing" },
      { id: "adl_toileting", question: "Toileting" },
      { id: "adl_transfers", question: "Transfers" },
      { id: "iadl_medications", question: "Medication management" },
      { id: "iadl_finances", question: "Finances" },
      { id: "iadl_transportation", question: "Transportation" },
    ],
  },
  mini_cog: {
    id: "mini_cog",
    title: "Mini-Cog",
    type: "single_choice",
    options: [
      { value: 0, label: "0 recalled words" },
      { value: 1, label: "1 recalled word" },
      { value: 2, label: "2 recalled words" },
      { value: 3, label: "3 recalled words" },
    ],
    stem: "Step 1: Register 3 words and ask for delayed recall. Step 2: Perform clock drawing.",
    extra: [{ id: "clock_draw_normal", label: "Clock draw is normal", type: "yes_no" }],
    items: [
      {
        id: "recall_score",
        question:
          "After saying and registering 3 words (example set: BANANA, SUNRISE, CHAIR), how many words were recalled after the delay?",
      },
    ],
  },
  moca_structure: {
    id: "moca_structure",
    title: "MoCA (Structure)",
    type: "numeric",
    min: 0,
    max: 30,
    items: [{ id: "total_score", question: "Total MoCA score (0-30)" }],
    helpText: "Structured score capture only (full MoCA text is copyrighted).",
  },
  cam: {
    id: "cam",
    title: "CAM (Confusion Assessment Method)",
    type: "yes_no",
    stem: "Assess each CAM feature using current clinical presentation.",
    items: [
      { id: "feature1", question: "Feature 1: Acute onset and fluctuating course present." },
      { id: "feature2", question: "Feature 2: Inattention present." },
      { id: "feature3", question: "Feature 3: Disorganized thinking present." },
      { id: "feature4", question: "Feature 4: Altered level of consciousness present." },
    ],
  },
  steadi: {
    id: "steadi",
    title: "CDC STEADI Screening",
    type: "yes_no",
    stem: "CDC STEADI screening questions:",
    items: [
      { id: "fell_past_year", question: "Have you fallen in the past year?" },
      { id: "unsteady", question: "Do you feel unsteady when standing or walking?" },
      { id: "worry_falling", question: "Do you worry about falling?" },
    ],
  },
  phq9: {
    id: "phq9",
    title: "PHQ-9",
    type: "single_choice",
    options: PHQ9_OPTIONS,
    stem: "Over the last 2 weeks, how often have you been bothered by the following problems?",
    items: [
      { id: "phq1", question: "Little interest or pleasure in doing things" },
      { id: "phq2", question: "Feeling down, depressed, or hopeless" },
      { id: "phq3", question: "Trouble falling or staying asleep, or sleeping too much" },
      { id: "phq4", question: "Feeling tired or having little energy" },
      { id: "phq5", question: "Poor appetite or overeating" },
      { id: "phq6", question: "Feeling bad about yourself - or that you are a failure or have let yourself or your family down" },
      { id: "phq7", question: "Trouble concentrating on things, such as reading the newspaper or watching television" },
      { id: "phq8", question: "Moving or speaking so slowly that other people could have noticed? Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual" },
      { id: "phq9", question: "Thoughts that you would be better off dead or of hurting yourself in some way" },
    ],
  },
  frail: {
    id: "frail",
    title: "FRAIL",
    type: "yes_no",
    items: [
      { id: "fatigue", question: "Fatigue: Do you feel tired most or all of the time?" },
      { id: "resistance", question: "Resistance: Do you have difficulty climbing one flight of stairs?" },
      { id: "ambulation", question: "Ambulation: Do you have difficulty walking one block?" },
      { id: "illnesses", question: "Illnesses: Do you have five or more chronic illnesses?" },
      { id: "weight_loss", question: "Loss of weight: Have you lost more than 5% of your weight in the last year?" },
    ],
  },
  mna_sf: {
    id: "mna_sf",
    title: "MNA-SF",
    type: "single_choice",
    items: [
      {
        id: "food_intake",
        question: "Has food intake declined over the past 3 months?",
        options: [
          { value: 0, label: "Severe decrease in food intake" },
          { value: 1, label: "Moderate decrease in food intake" },
          { value: 2, label: "No decrease in food intake" },
        ],
      },
      {
        id: "weight_loss",
        question: "Weight loss during the last 3 months",
        options: [
          { value: 0, label: "Weight loss greater than 3 kg (6.6 lb)" },
          { value: 1, label: "Does not know" },
          { value: 2, label: "Weight loss between 1 and 3 kg (2.2-6.6 lb)" },
          { value: 3, label: "No weight loss" },
        ],
      },
      {
        id: "mobility",
        question: "Mobility",
        options: [
          { value: 0, label: "Bed or chair bound" },
          { value: 1, label: "Able to get out of bed/chair but does not go out" },
          { value: 2, label: "Goes out" },
        ],
      },
      {
        id: "stress",
        question: "Has suffered psychological stress or acute disease in the past 3 months?",
        options: [{ value: 0, label: "Yes" }, { value: 2, label: "No" }],
      },
      {
        id: "neuropsych",
        question: "Neuropsychological problems",
        options: [
          { value: 0, label: "Severe dementia or depression" },
          { value: 1, label: "Mild dementia" },
          { value: 2, label: "No psychological problems" },
        ],
      },
      {
        id: "bmi",
        question: "Body mass index (BMI)",
        options: [
          { value: 0, label: "BMI less than 19" },
          { value: 1, label: "BMI 19 to less than 21" },
          { value: 2, label: "BMI 21 to less than 23" },
          { value: 3, label: "BMI 23 or greater" },
        ],
      },
    ],
  },
  sdoh_cms: {
    id: "sdoh_cms",
    title: "Social Risk Screening (CMS-style domains)",
    type: "yes_no",
    stem: "In the last 12 months, did the patient report any of the following social needs?",
    items: [
      { id: "food_insecurity", question: "Worried food would run out before there was money to buy more." },
      { id: "housing_instability", question: "Housing instability risk (homeless, shelter, or unable to maintain stable housing)." },
      { id: "transportation", question: "Transportation problems kept the patient from medical visits or daily needs." },
      { id: "utilities", question: "Utility shutoff risk (electric, gas, water, or heating)." },
      { id: "safety", question: "Personal safety concern or inadequate social support at home." },
    ],
  },
};

export const USA_WORKFLOW_ORDER = [
  "what_matters",
  "medication_review",
  "adl_iadl",
  "mini_cog",
  "steadi",
  "phq9",
  "frail",
  "mna_sf",
  "sdoh_cms",
];

export function scoreUsInstrument(id, answers = {}) {
  if (id === "mini_cog") {
    const recall = Number(answers.recall_score ?? 0);
    const clock = answers.clock_draw_normal ? 2 : 0;
    const total = recall + clock;
    return {
      score: total,
      interpretation:
        total <= 2
          ? "Mini-Cog screening concern; follow-up cognitive assessment is recommended."
          : "Mini-Cog lower-concern result on screening.",
      nextStep:
        total <= 2
          ? "Schedule focused cognitive follow-up and review safety/medication factors."
          : "Continue routine cognitive screening at future visits.",
    };
  }
  if (id === "moca_structure") {
    const score = Number(answers.total_score ?? 0);
    return {
      score,
      interpretation: score < 26 ? "MoCA score suggests cognitive screening concern." : "MoCA score is in a lower-concern range.",
      nextStep: score < 26 ? "Correlate with function/history and plan follow-up cognitive assessment." : "Continue periodic cognitive monitoring.",
    };
  }
  if (id === "cam") {
    const f1 = !!answers.feature1;
    const f2 = !!answers.feature2;
    const f3 = !!answers.feature3;
    const f4 = !!answers.feature4;
    const positive = f1 && f2 && (f3 || f4);
    return {
      score: positive ? 1 : 0,
      interpretation: positive
        ? "CAM screening pattern present (Feature 1 + 2 + [3 or 4]); urgent clinical evaluation is recommended."
        : "CAM screening pattern not present.",
      nextStep: positive
        ? "Escalate to urgent delirium-focused clinical evaluation and treat reversible contributors."
        : "Reassess CAM if acute mental-status changes occur.",
    };
  }
  if (id === "phq9") {
    const score = Object.keys(answers).reduce((sum, key) => sum + Number(answers[key] ?? 0), 0);
    let severityBand = "minimal";
    if (score >= 20) severityBand = "severe";
    else if (score >= 15) severityBand = "moderately severe";
    else if (score >= 10) severityBand = "moderate";
    else if (score >= 5) severityBand = "mild";
    return {
      score,
      interpretation: `PHQ-9 severity: ${severityBand}.`,
      severityBand,
      nextStep:
        score >= 10
          ? "Consider structured mood follow-up and safety check based on clinical context."
          : "Continue routine mood screening follow-up.",
    };
  }
  if (id === "steadi") {
    const score = Object.keys(answers).reduce((sum, key) => sum + (answers[key] ? 1 : 0), 0);
    let interpretation = "STEADI low fall-risk screen";
    if (score >= 2) interpretation = "STEADI high fall-risk screen; assess gait, balance, and modifiable risks.";
    else if (score === 1) interpretation = "STEADI positive screen; targeted fall-risk assessment is recommended.";
    return {
      score,
      interpretation,
      nextStep:
        score >= 2
          ? "Start fall-risk intervention plan and schedule close follow-up."
          : score === 1
            ? "Perform targeted gait/balance and medication-risk review."
            : "Reinforce fall-prevention counseling and routine screening.",
    };
  }
  if (id === "sdoh_cms") {
    const score = Object.keys(answers).reduce((sum, key) => sum + (answers[key] ? 1 : 0), 0);
    let interpretation = "No positive SDOH domains identified.";
    if (score >= 2) interpretation = "Multiple SDOH needs identified; prioritize support linkage and follow-up.";
    else if (score === 1) interpretation = "Single SDOH need identified; consider targeted resource referral.";
    return {
      score,
      interpretation,
      nextStep:
        score >= 2
          ? "Prioritize social-work/resource linkage and set follow-up check-in."
          : score === 1
            ? "Address the positive domain and connect to targeted support."
            : "Continue periodic social-risk screening.",
    };
  }
  if (id === "frail") {
    const score = Object.keys(answers).reduce((sum, key) => sum + (answers[key] ? 1 : 0), 0);
    return {
      score,
      interpretation: score >= 3 ? "FRAIL positive screening pattern." : score >= 1 ? "FRAIL intermediate screening concern." : "FRAIL lower-concern screen.",
      nextStep:
        score >= 3
          ? "Review mobility, nutrition, and strength plan with short-interval follow-up."
          : score >= 1
            ? "Address modifiable frailty contributors and monitor trend."
            : "Continue routine frailty screening.",
    };
  }
  if (id === "what_matters") {
    const score = Object.keys(answers).reduce((sum, key) => sum + (answers[key] ? 1 : 0), 0);
    return {
      score,
      interpretation: score >= 2 ? "What Matters documentation complete." : "What Matters documentation incomplete.",
      nextStep: score >= 2 ? "Keep preferences updated at follow-up visits." : "Complete patient-priority and decision-maker documentation.",
    };
  }
  if (id === "medication_review") {
    const score = Object.keys(answers).reduce((sum, key) => sum + (answers[key] ? 1 : 0), 0);
    return {
      score,
      interpretation: score >= 3 ? "Medication review documentation complete." : "Medication review documentation partially complete.",
      nextStep: score >= 3 ? "Continue periodic reconciliation and risk review." : "Complete reconciliation and high-risk medication safety review.",
    };
  }
  if (id === "adl_iadl") {
    const score = Object.keys(answers).reduce((sum, key) => sum + Number(answers[key] ?? 0), 0);
    let interpretation = "Independent across documented ADL/IADL domains.";
    if (score >= 8) interpretation = "High dependency profile; coordinated functional support planning is recommended.";
    else if (score >= 3) interpretation = "Functional assistance needs identified in one or more domains.";
    return {
      score,
      interpretation,
      nextStep:
        score >= 8
          ? "Coordinate caregiver/support services and functional follow-up."
          : score >= 3
            ? "Target support in affected ADL/IADL domains and reassess."
            : "Maintain independence plan and routine monitoring.",
    };
  }
  if (id === "mna_sf") {
    const score = Object.keys(answers).reduce((sum, key) => sum + Number(answers[key] ?? 0), 0);
    let interpretation = "Normal nutritional status";
    if (score <= 7) interpretation = "Malnutrition concern on screening";
    else if (score <= 11) interpretation = "At risk of malnutrition";
    return {
      score,
      interpretation,
      nextStep:
        score <= 11
          ? "Review intake/weight trend and arrange nutrition-focused follow-up."
          : "Continue periodic nutrition screening.",
    };
  }
  return { score: 0, interpretation: "", nextStep: "" };
}
